import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Actions as EMPLOYEEACTIONS } from '../../../../store/actions/employeeActions';
import { format, addMonths, lastDayOfMonth, getYear } from 'date-fns';
import { useHistory } from "react-router-dom";
import Tooltip from "@/tooltip"
import { getEmploymentTypeName, getPayFrequencyName, validateFloat } from '../../../common/validation/Validators';

const cola = {
    '2022': {
        'Hour': 0.0438, //75?
        'Week': 1.75,
        'Fortnight (Two Weeks)': 3.5,
        'Four Weeks': 7,
        'Month': 7.5833,
        'Year': 91,
    },
    '2023': {
        'Hour': 0.2475,
        'Week': 9.90,
        'Fortnight (Two Weeks)': 19.8,
        'Four Weeks': 39.6,
        'Month': 42.9,
        'Year': 514.8,
    },
    '2024': {
        'Hour': 0.32025,
        'Day': 2.562,
        'Week': 12.81,
        'Fortnight (Two Weeks)': 25.62,
        'Four Weeks': 51.24,
        'Month': 55.51,
        'Year': 666.12,
        'estimated': false
    }
}

const minimumWage = {
    '2023': {
        increaseAfterYearsOfService:{
            0: {
                'Hour': 0,
                'Day': 0,
                'Week': 0,
                'Fortnight (Two Weeks)': 0,
                'Four Weeks': 0,
                'Month': 0,
                'Year': 0,
            },
            // 1: {
            //     'Hour': 0.075,
            //     'Day': 0.6,
            //     'Week': 3,
            //     'Fortnight (Two Weeks)': 6,
            //     'Four Weeks': 12,
            //     'Month': 13,
            //     'Year': 156,
            // },
            // 2: {
            //     'Hour': 0.15,
            //     'Day': 1.2,
            //     'Week': 6,
            //     'Fortnight (Two Weeks)': 12,
            //     'Four Weeks': 24,
            //     'Month': 26,
            //     'Year': 312,
            // },
        },
        // setup using age so that we factor in data that is organised differently
        'age': {
            '16': {
                'Week': 183.11,
                'Fortnight (Two Weeks)': 366.22,
                'Four Weeks': 732.44
            },
            '17':{
                'Week': 185.95,
                'Fortnight (Two Weeks)': 371.9,
                'Four Weeks': 743.8,
            },
            '18':{
                'Hour': 4.81825,
                'Day': 38.546,
                'Week': 192.73,
                'Fortnight (Two Weeks)': 385.46,
                'Four Weeks': 770.92,
                'Month': 835.1633,
                'Year': 10021.96,
            }
        }
    },
    '2024': {
        increaseAfterYearsOfService:{
            0: {
                'Hour': 0,
                'Day': 0,
                'Week': 0,
                'Fortnight (Two Weeks)': 0,
                'Four Weeks': 0,
                'Month': 0,
                'Year': 0,
            },
            // 1: {
            //     'Hour': 0.075,
            //     'Day': 0.6,
            //     'Week': 3,
            //     'Fortnight (Two Weeks)': 6,
            //     'Four Weeks': 12,
            //     'Month': 13,
            //     'Year': 156,
            // },
            // 2: {
            //     'Hour': 0.15,
            //     'Day': 1.2,
            //     'Week': 6,
            //     'Fortnight (Two Weeks)': 12,
            //     'Four Weeks': 24,
            //     'Month': 26,
            //     'Year': 312,
            // },
        },
        // setup using age so that we factor in data that is organised differently
        'age': {
            '16': {

            },
            '17':{

            },
            '18':{
                'Hour': 5.3385,
                'Day': 42.708,
                'Week': 213.54,
                'Fortnight (Two Weeks)': 427.08,
                'Four Weeks': 854.16,
                'Month': 925.34,
                'Year': 11104.08,
            }
        }
    }
}

const parseLocaleNumber = strNum => {
    if (typeof strNum !== 'string') return strNum;
    const decSep = (1.1).toLocaleString().substring(1, 2);
    const formatted = strNum
        .replace(new RegExp(`([${decSep}])(?=.*\\1)`, 'g'), '')
        .replace(new RegExp(`[^0-9${decSep}]`, 'g'), '');
    return Number(formatted.replace(decSep, '.'));
};

// this is based on the year in the course
const apprenticeMinimumWagePortion = {
    1: 0.2593360996,
    2: 0.2717842324,
    3: 0.3817427386
}

const currencyFormatStyle = { minimumFractionDigits: 0, maximumFractionDigits: 4, currency: "EUR", style:"currency" };
const numberFormatStyle = { minimumFractionDigits: 0, maximumFractionDigits: 2};


const EmployeeRow = ({
    employee,
    setEmployeeCOLA,
    newSalary,
    currency,
    colaRounding,
    colaPreApplied,
    referenceDate,
    currentSalary,
    year
}) => {
    const doe = employee.engagementDate
    const termination = employee.terminationDate
    const payFrequency = getPayFrequencyName({ payFrequency: currentSalary?.payPeriodFrequency })
    const employmentTypeName = getEmploymentTypeName({ employmentType: currentSalary?.employmentType })
    const colaRoundingPoint = colaRounding ? 2 : 8
    const employeeName = `${employee.employeeName} ${employee.employeeSurname}`

    const calculateEntitlement = () => {
        const weeklyHours = (parseFloat(currentSalary?.weeklyHours) || 1)
        const workingDays = (parseFloat(currentSalary?.workingDays?.length) || 1)
        const yearlyHours = weeklyHours * 52
        const dailyHours = weeklyHours / workingDays
        const monthlyHours = yearlyHours / 12
        const monthlyAverage = 2080 / 12
        const entitled = cola?.[year]?.[payFrequency == 'Day' ? 'Hour' : payFrequency]

        // calculate exact age from date of birth
        // console.log('employee',employee)
        const age = Math.floor((new Date(referenceDate) - new Date(employee.dateOfBirth).getTime()) / 3.15576e+10)
        // sort minimum wage ages in descending order
        const minimumWageAges = Object.keys(minimumWage?.[year]?.['age']).sort((a, b) => b - a)
        const ageToUse = minimumWageAges.find(minimumWageAge => minimumWageAge <= age) || minimumWageAges[minimumWageAges.length - 1]

        // TODO calculate any additions required due to minimum wage setup
        let minimumWageApplicable = minimumWage?.[year]?.['age']?.[ageToUse]?.[payFrequency]
        const yearsOfService = Math.floor((new Date(referenceDate) - new Date(doe).getTime()) / 3.15576e+10)
        const salary = parseFloat(currentSalary?.salaryAmount || 0)

        const increaseAfterYearsKeys = Object.keys(minimumWage?.[year]?.['increaseAfterYearsOfService']).sort((a, b) => b - a)
        const increaseAfterYearsOfServiceYear = increaseAfterYearsKeys.find(year => yearsOfService >= year)
        const increaseAfterYearsOfService = minimumWage?.[year]?.['increaseAfterYearsOfService']?.[increaseAfterYearsOfServiceYear]?.[payFrequency]
        if (increaseAfterYearsOfService) {
            minimumWageApplicable += increaseAfterYearsOfService
            console.log({increaseAfterYearsOfService})
        }

        // correct minimum wage based on working hours
        if (currentSalary?.weeklyHours && !['Day','Hour'].includes(payFrequency)){
            minimumWageApplicable = minimumWageApplicable / 40 * weeklyHours
        }

        if (payFrequency == 'Day') {
            let currentRate = entitled * dailyHours
            const minimumWageDifference = (currentRate + salary) - minimumWageApplicable
            if (minimumWageDifference < 0) {
                currentRate = minimumWageApplicable - salary
            }
            return {
                currentRate,
                content: [
                    'Employee Works:',
                    '-----------------------------------------------',
                    `${weeklyHours} Hours per Week`,
                    `${workingDays} Days per Week`,
                    `${dailyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Day on Average`,
                    '-----------------------------------------------',
                    `€${cola?.[year]?.['Hour']} COLA per Hour * ${dailyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Day`,
                    '-----------------------------------------------',
                    `${(currentRate).toLocaleString(undefined, currencyFormatStyle)} Entitlement per Day`,
                    '-----------------------------------------------',
                ]
            }
        }

        switch (payFrequency) {
            case 'Four Weeks':
            case 'Fortnight (Two Weeks)':
            case 'Week': {
                let currentRate = entitled * (weeklyHours / 40)
                const minimumWageDifference = (currentRate + salary) - minimumWageApplicable

                const content = [
                    'Employee Works:',
                    '-----------------------------------------------',
                    `${weeklyHours} Hours per Week`,
                    '-----------------------------------------------',
                    `€${cola?.[year]?.['Hour']} COLA per Hour * ${weeklyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Week`,
                    '-----------------------------------------------',
                    `${currentRate?.toLocaleString(undefined, currencyFormatStyle)} Entitlement per ${payFrequency}`,
                    '-----------------------------------------------',
                ]
                if (minimumWageDifference < 0) {
                    currentRate = minimumWageApplicable - salary
                    if (increaseAfterYearsOfService){
                        content.push(
                            `${increaseAfterYearsOfService?.toLocaleString(undefined, currencyFormatStyle)} added to minimum wage for ${yearsOfService} Years of Service`
                        )
                    }
                    content.push(
                        `${minimumWageApplicable?.toLocaleString(undefined, currencyFormatStyle)} Minimum Wage per ${payFrequency}`,
                        '-----------------------------------------------',
                        `Adjusted Increase: ${currentRate.toLocaleString(undefined, currencyFormatStyle)}`,
                        '-----------------------------------------------',
                    )
                }
                return {
                    currentRate,
                    content
                }
            }
            case 'Month': {
                let currentRate = entitled * (monthlyHours / monthlyAverage)
                const minimumWageDifference = (currentRate + salary) - minimumWageApplicable
                const content = [
                    'Employee Works:',
                    '-----------------------------------------------',
                    `${weeklyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Week`,
                    `${yearlyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Year`,
                    `${monthlyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Month`,
                    '-----------------------------------------------',
                    `€${cola?.[year]?.['Hour']} COLA per Hour *`,
                    `${monthlyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Month`,
                    '-----------------------------------------------',
                    `${currentRate.toLocaleString(undefined, currencyFormatStyle)} Entitlement per Month`,
                    '-----------------------------------------------',
                ]
                if (minimumWageDifference < 0) {
                    currentRate = minimumWageApplicable - salary
                    if (increaseAfterYearsOfService){
                        content.push(
                            `${increaseAfterYearsOfService?.toLocaleString(undefined, currencyFormatStyle)} added to minimum wage for ${yearsOfService} Years of Service`
                        )
                    }
                    content.push(
                        `${minimumWageApplicable?.toLocaleString(undefined, currencyFormatStyle)} Minimum Wage per Month`,
                        '-----------------------------------------------',
                        `Adjusted Increase: ${currentRate.toLocaleString(undefined, currencyFormatStyle)}`,
                        '-----------------------------------------------',
                    )
                }
                return {
                    currentRate,
                    content
                }
            }
            case 'Year': {
                let currentRate = entitled * (yearlyHours / 2080)
                const minimumWageDifference = (currentRate + salary) - minimumWageApplicable
                const content = [
                    'Employee Works:',
                    '-----------------------------------------------',
                    `${weeklyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Week`,
                    `${yearlyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Year`,
                    '-----------------------------------------------',
                    `€${cola?.[year]?.['Hour']} COLA per Hour *`,
                    `${yearlyHours.toLocaleString(undefined, numberFormatStyle)} Hours per Year`,
                    '-----------------------------------------------',
                    `${currentRate.toLocaleString(undefined, currencyFormatStyle)} Entitlement per Year`,
                    '-----------------------------------------------',
                ]
                if (minimumWageDifference < 0) {
                    currentRate = minimumWageApplicable - salary
                    if (increaseAfterYearsOfService){
                        content.push(
                            `${increaseAfterYearsOfService?.toLocaleString(undefined, currencyFormatStyle)} added to minimum wage for ${yearsOfService} Years of Service`
                        )
                    }
                    content.push(
                        `${minimumWageApplicable?.toLocaleString(undefined, currencyFormatStyle)} Minimum Wage per Month`,
                        '-----------------------------------------------',
                        `Adjusted Increase: ${currentRate.toLocaleString(undefined, currencyFormatStyle)}`,
                        '-----------------------------------------------',
                    )
                }
                return {
                    currentRate,
                    content
                }
            }
            default: {
                let currentRate = entitled
                const minimumWageDifference = (currentRate + salary) - minimumWageApplicable
                if (minimumWageDifference < 0) {
                    currentRate = minimumWageApplicable - salary
                }
                return {currentRate}
            }
        }
    }

    const salaryAmount = parseFloat(currentSalary?.salaryAmount || 0);
    const { content, currentRate } = calculateEntitlement()

    const decimalFactor = Math.pow(10, (colaRoundingPoint ?? 4))
    const applyCola = colaPreApplied ? 0 : (Math.ceil(currentRate * decimalFactor)/decimalFactor)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: Math.min(colaRoundingPoint, 5) })
    console.log({applyCola,decimalFactor})
    const salaryWithCola = salaryAmount + parseLocaleNumber(applyCola)
    // const minimumWageDifference = minimumWageApplicable - salaryWithCola
    const difference = (Math.ceil((newSalary - salaryAmount)* decimalFactor)/decimalFactor).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: Math.min(colaRoundingPoint, 5) })


    useEffect(() => {
        const decimalFactor = Math.pow(10, (colaRoundingPoint ?? 4))
        let salary = (Math.ceil(parseFloat((parseFloat(salaryAmount) + parseLocaleNumber(applyCola)) * decimalFactor).toFixed(4))/decimalFactor).toFixed(colaRoundingPoint)
        if (colaRoundingPoint === 8) salary = parseFloat(salary).toFixed(5);
        setEmployeeCOLA(employee.id, { salary: parseFloat(salary), valid: true })
    }, [salaryAmount, colaRounding, colaPreApplied])

    return (
        <tr>
            <td>
                {employeeName?.length > 15 ?
                    <Tooltip content={employeeName}>
                        <p style={{ marginBottom: 0 }}>{employeeName?.substring(0, 15) + '...'}</p>
                    </Tooltip>
                    : employeeName
                }
            </td>
            <td>{format(new Date(doe), 'dd/MM/yyyy')} {termination ? `- ${format(new Date(termination), 'dd/MM/yyyy')} ` : null}</td>
            <td>{employmentTypeName}</td>
            <td>{currency}{salaryAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/{payFrequency}</td>
            <td>
                {currency}
                <input
                    style={{ display: "inline-block", width: "70px", padding: "0 5px" }}
                    value={newSalary}
                    onChange={(e) => {
                        setEmployeeCOLA(employee.id, { salary: validateFloat(e.target.value), valid: validateFloat(e.target.value) >= salaryWithCola })
                    }}
                />
                /{payFrequency}
            </td>
            {!colaPreApplied && (content || difference < applyCola) ?
                <td>
                    <Tooltip
                        content={difference < applyCola ?
                            `Salary Increase cannot be less than ${currency}${applyCola}`
                            :
                            <div style={{ whiteSpace: "nowrap" }}>
                                <p style={{ color: 'white', fontWeight: 700 }}>Entitlement Explained</p>
                                <ul style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                                    {content?.map(item => {
                                        return (
                                            <li>{item}</li>
                                        )
                                    })}
                                </ul>
                            </div>
                        }>
                        <p style={{ color: difference < applyCola ? 'red' : 'inherit', marginBottom: 0 }}>{currency}{difference}/{payFrequency}</p>
                    </Tooltip>
                </td>
                :
                <td>
                    {currency}{difference}/{payFrequency}
                </td>
            }

        </tr>
    );
};

const mapStateToProps = (state, { employee }) => {

    const cola = state.cola
    const currentSalary = cola.sortedEmployees?.find(sorted => sorted?.id == employee?.id)?.salary

    return {
        newSalary: cola.salaries?.[employee?.id]?.salary,
        colaRounding: cola.rounding,
        colaPreApplied: cola.preApplied,
        referenceDate: cola.referenceDate,
        currency: state.company?.settings?.currency,
        currentSalary
    };
};

const mapDispatchToProps = (dispatch) => ({
    setEmployeeCOLA: (agreementId, value) => dispatch(EMPLOYEEACTIONS.setEmployeeCOLA(agreementId, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeRow);
